/** ----------------处理性能曲线图标数据---------------- **/
export default class CurveData {
  // 符合条件的曲线集
  curves = []
  // 有值的转速集
  speeds = []
  xAxises = []
  yAxises = []
  // 抛物线
  parabolas = []
  // 运行点
  operatingData = null
  // 需求点
  designPropertyData = null
  // 调节曲线
  speedRegulationCurves = []
  // 由于类型变化导致坐标轴和抛物线变化
  axisChanged = false
  // 单位风量耗功率
  powerConsumptionPerAirVolume = null
  /**
   * 将接口数据处理成图表展示所需要的数据
   * @param productCurves: array[],性能曲线数据
   * @param designPoints: array[], 需求点信息
   * @param workConditionCurves: array[],工况相似抛物线数据
   * @param intersectionPoints: array[],运行点数据
   * @param speedRegulate: Object, 调节曲线数据，包括调节曲线，调节比例，调节运行点
   * @param type: String,枚举值:pump,fan
   * @param powerConsumptionPerAirVolume: Number, 单位风量耗功率
   */
  constructor(productCurves, designPoints, workConditionCurves, intersectionPoints, speedRegulate, type, powerConsumptionPerAirVolume) {
    this.axisChanged = type === 'pump' || 'C000007'
    this.powerConsumptionPerAirVolume = powerConsumptionPerAirVolume
    const { curves: regulateCurve, intersectionPoints: regulateIntersectionPoints, originRate, rate } = speedRegulate || {}
    // 处理曲线数据
    productCurves.forEach((item) => {
      // 若曲线有3+个非异常坐标点，则继续，否则过滤掉该曲线
      const accessPoints = item.fittingPoints || []
      if (accessPoints && accessPoints.length >= 3) {
        // 将坐标点提取[[x,y],[x,y]]的形式
        const pointData = accessPoints.map((point) => {
          return [point.valueX, point.valueY]
        })
        // 如果有运行点信息，处理运行点信息
        if (intersectionPoints && intersectionPoints.length) {
          const interObj = intersectionPoints.find((inter) => inter.curveId === item.id)
          if (interObj) {
            const points = [interObj.valueX, interObj.valueY]
            item.operatingPoint = points
            pointData.push(points)
          }
        }
        // 如果有转速，就检验是否重复，若不重复，则将转速提取出来
        let speedStr = null
        if (item.curvePropertyDefinitions) {
          const sObj = item.curvePropertyDefinitions.find((it) => it.code === 'PCD-P01') || {}
          if (sObj.value || sObj.value === 0) {
            speedStr = `${sObj.value}${sObj.unit || 'rpm'}`
            if (!this.speeds.includes(speedStr)) {
              this.speeds.push(speedStr)
            }
          }
        }
        // 判断并提取x轴信息
        if (!this.xAxises.some((x) => x.name === item.axisNameX)) {
          this.xAxises.push({
            name: item.axisNameX,
            unit: item.unitX,
            sort: item.sort
          })
        }
        // 判断并提取y轴信息
        if (!this.yAxises.some((y) => y.name === item.axisNameY)) {
          this.yAxises.push({
            name: item.axisNameY,
            unit: item.unitY,
            sort: item.sort
          })
        }
        // 提取曲线坐标点信息
        const pointObj = {
          speed: speedStr,
          xName: item.axisNameX,
          xUnit: item.unitX,
          yName: item.axisNameY,
          yUnit: item.unitY,
          operatingPoint: item.operatingPoint,
        }
        this.curves.push({
          ...pointObj,
          data: this.sortPoints(pointData)
        })
      }
    })
    this.calcXAxisesInfo()
    this.calcYAxisesInfo()
    // 处理需求点
    if (designPoints && designPoints.length) {
      this.handleDesignPropertyData(designPoints)
    }
    // 处理工况相似抛物线数据
    if (workConditionCurves && workConditionCurves.length) {
      this.handleParabolaData(workConditionCurves)
    }
    // 处理运行点
    if (intersectionPoints && intersectionPoints.length) {
      this.handleOperatingData(intersectionPoints, regulateIntersectionPoints, originRate, rate)
    }
    // 处理调节曲线信息
    if (regulateCurve && regulateCurve.length) {
      this.handleRegulateData(regulateCurve, regulateIntersectionPoints)
    }
  }

  // 获取拟合曲线所需的数据
  getCurveData() {
    return {
      curves: this.curves,
      speedRegulationCurves: this.speedRegulationCurves,
      speeds: this.speeds,
      xAxises: this.xAxises,
      yAxises: this.yAxises,
      designPropertyData: this.designPropertyData,
      parabolas: this.parabolas,
      operatingData: this.operatingData,
      powerConsumptionPerAirVolume: this.powerConsumptionPerAirVolume
    }
  }

  // 根据坐标轴名称和类型，计算坐标轴的步长，起始值和终点值; axisesType: x、y
  // 返回值：{startValue, endValue, step}
  analyseAxises(axisesName, axisesType) {
    const nameKey = axisesType.toLowerCase() === 'x' ? 'xName' : 'yName'
    const valueKey = axisesType.toLowerCase() === 'x' ? 0 : 1
    let res = []
    const curves = this.curves.filter((c) => c[nameKey] === axisesName) || []
    curves.forEach((item) => {
      const valueArr = item.data.map((d) => d[valueKey])
      res = [...res, ...valueArr]
    })
    if (!res.length) { return {} }
    const maxValue = Math.max(...res)
    const minValue = Math.min(...res)
    const commonAxisMain = this.getAxisMain(minValue, maxValue, axisesType) 
    const specAxis = this.axisChanged ? { 流量: 2, 扬程: 4, 轴功率: 8 } : null
    if (specAxis) {
      const specKeys = Object.keys(specAxis)
      if (specKeys.includes(axisesName)) {
        const times = specAxis[axisesName]
        const minReguValue = Math.ceil(minValue / times) 
        const { startValue: rStartValue, endValue: rEndValue, step: rStep } = this.getAxisMain(minReguValue, maxValue, axisesType)
        return { ...commonAxisMain, rStartValue, rEndValue, rStep }
      }
    } 
    return commonAxisMain
  }

  // 根据规则计算起始值、终止值和步长
  getAxisMain(min, max, axisesType) {
    const count = axisesType.toLowerCase() === 'x' ? 8 : 6
    // 根据最大最小值计算步长
    let step = Math.ceil((max - min) / count)
    if (step > 10) {
      step = Math.ceil(step / 10) * 10
    }
    const startValue = min - step < 0 ? 0 : Math.ceil(min - step)
    const endValue = (count + 2) * step + startValue
    return { startValue, endValue, step }
  }

  // 计算各x轴坐标轴信息
  calcXAxisesInfo() {
    this.xAxises.forEach((item) => {
      const { startValue, endValue, step, rStartValue, rEndValue, rStep } = this.analyseAxises(item.name, 'x')
      item.min = startValue
      item.max = endValue
      item.interval = step
      if (rStartValue || rStartValue === 0) {
        item.rMin = rStartValue
      }
      if (rEndValue || rEndValue === 0) {
        item.rMax = rEndValue
      }
      if (rStep || rStep === 0) {
        item.rInterval = rStep
      }
    })
  }

  // 计算各y轴坐标轴信息
  calcYAxisesInfo() {
    this.yAxises.forEach((item) => {
      if (item.name.includes('效率')) {
        // 效率曲线y轴范围特殊处理
        item.min = 20
        item.max = 100
        item.interval = 10
      } else {
        const { startValue, endValue, step, rStartValue, rEndValue, rStep } = this.analyseAxises(item.name, 'y')
        item.min = startValue
        item.max = endValue
        item.interval = step
        if (rStartValue || rStartValue === 0) {
          item.rMin = rStartValue
        }
        if (rEndValue || rEndValue === 0) {
          item.rMax = rEndValue
        }
        if (rStep || rStep === 0) {
          item.rInterval = rStep
        }
      }
    })
  }

  /**
   * 工况相似抛物线：
   * valueK是null 输出的应该是一条x=x1的直线
   * valueK不是null 前端根据y=kx^2算出抛物线
   * 
   */
  handleParabolaData(workConditionCurves) {
    // 取x值，计算出y值；x值的取点规则：x轴的坐标值+需求点+运行点
    // regulatedData：水泵在调速之后点会根据坐标轴变化而变化
    let valueX1, valueX2
    const { min, max, interval, rMin, rMax, rInterval } = this.xAxises[0]
    const { min: yMin, max: yMax, interval: yInterval } = this.yAxises[0]
    workConditionCurves.forEach((obj) => {
      const k = obj.valueK
      if (k !== null) {
        if (!valueX1) {
          valueX1 = this.getParabolaAxisValue(min, max, interval)
        }
        const calcPoints1 = valueX1.map((x) => {
          const y = Math.round(k * x * x * 100) / 100
          return [x, y]
        })
        const parabola = {
          data: this.sortPoints([...calcPoints1, obj.needPoints]),
          designPoint: obj.needPoints
        }
        if (this.axisChanged) {
          if (!valueX2) {
            valueX2 = this.getParabolaAxisValue(rMin, rMax, rInterval)
          }
          const calcPoints2 = valueX2.map((x) => {
            const y = Math.round(k * x * x * 100) / 100
            return [x, y]
          })
          parabola.regulatedData = this.sortPoints([...calcPoints2, obj.needPoints])
        }
        this.parabolas.push(parabola)
      } else {
        const valueY = this.getParabolaAxisValue(yMin, yMax, yInterval)
        if (Array.isArray(obj.needPoints)) {
          const valueX = obj.needPoints[0]
          const calcPoints = valueY.map((y) => [valueX, y])
          const parabola = {
            data: calcPoints,
            designPoint: null
          }
          this.parabolas.push(parabola)
        }
      }
      console.log('this.parabolas:', this.parabolas)
    })
  }

  // 获取抛物线x或y的取值点
  getParabolaAxisValue(min, max, interval) {
    const valueX = []
    for (let i = 0; i <= 10; i++) {
      const current = min + (interval * i)
      if (current <= max) {
        valueX.push(current)
      }
    }
    return valueX
  }

  // 处理运行点
  handleOperatingData(intersectionPoints, regulateIntersectionPoints, originRate, rate) {
    regulateIntersectionPoints = regulateIntersectionPoints || []
    let tableColumns = []
    const tableData = []
    const regulationTableData = []
    intersectionPoints.forEach((item) => {
      // 先处理tableColumns列信息：如果tableColumns为空数组，则取第一个item的speed，x轴信息；否则，只需取y轴信息
      if (!tableColumns.length) {
        const cols = [
          {
            prop: 'speed',
            label: item.unitSpeed ? `转速(${item.unitSpeed})` : '转速',
          }, {
            prop: item.nameX,
            label: item.unitX ? `${item.nameX}(${item.unitX})` : `${item.nameX}`,
          }, {
            prop: item.nameY,
            label: item.unitY ? `${item.nameY}(${item.unitY})` : `${item.nameY}`,
          }
        ]
        tableColumns = [...cols]
      } else {
        // 先判断tableColumns中是否已存在y轴信息，不存在就新增
        const colObj = tableColumns.find((col) => col.prop === item.nameY)
        if (!colObj) {
          tableColumns.push({
            prop: item.nameY,
            label: item.unitY ? `${item.nameY}(${item.unitY})` : `${item.nameY}`,
          })
        }
      }
      // 处理tableData；判断是否有相同的转速和风量，有，就直接在该元素中添加数据，没有，就新创建元素；
      const dataItem = tableData.find((td) => td.speed === item.speed && td[item.nameX] === item.valueX)
      if (dataItem) {
        dataItem[item.nameY] = item.valueY
      } else {
        tableData.push({
          speed: item.speed,
          [item.nameX]: item.valueX,
          [item.nameY]: item.valueY,
        })
      }
    })
    // 水泵调速后的运行点处理
    const xObj = regulateIntersectionPoints.find((p) => p.nameY === '扬程') || {}
    regulateIntersectionPoints.forEach((item) => {
      const dataItem = regulationTableData[0]
      if (dataItem) {
        dataItem[item.nameY] = item.valueY
      } else {
        regulationTableData.push({
          speed: item.speed,
          [item.nameX]: xObj.valueX,
          [item.nameY]: item.valueY,
        })
      }
    })
    this.operatingData = {
      name: '运行点',
      tableColumns,
      tableData,
      regulationTableData,
      originRate,
      rate: rate || rate === 0 ? rate : null
    }
  }

  // 处理需求点
  handleDesignPropertyData(designPoints) {
    if (designPoints && designPoints.length) {
      if (designPoints.some((p) => p.value !== null)) { 
        this.designPropertyData = {
          name: '需求点',
          values: designPoints.map((item) => {
            return {
              label: item.label,
              value: item.value || '-'
            }
          })
        }
      } else {
        this.designPropertyData = null
      }
    }
  }

  // 处理调节曲线
  handleRegulateData(curves, intersectionPoints) {
    curves.forEach((item) => {
      const data = item.points.map((p) => [p.valueX, p.valueY])
      const operatingPointObj = intersectionPoints.find((interPoint) => interPoint.curveId === item.curveId) || {}
      const operatingPoint = [operatingPointObj.valueX, operatingPointObj.valueY]
      this.speedRegulationCurves.push({
        data: this.sortPoints([...data, operatingPoint]),
        speed: `${item.speed}${item.unitSpeed || 'rpm'}`,
        xName: item.nameX,
        xUnit: item.unitX,
        yName: item.nameY,
        yUnit: item.unitY,
        operatingPoint: operatingPoint
      })
    })
  }

  // 坐标点按x值从小到大排序
  sortPoints(points) {
    return points.sort((a, b) => { return a[0] - b[0] })
  }
}

